import {
  SanityProductFragment,
  SanityProductVariantFragment,
} from '@data/sanity/queries/types/product'
import { SanityGeneralSettings } from '@data/sanity/queries/types/site'
import { useUser } from '@lib/auth'
import { getPriceNumber } from '@lib/helpers'
import { Locale } from '@lib/language'
import { getFormattedProductTitle } from '@lib/product'
import { getAbsoluteProductUrl, getProductImageUrl } from '@lib/product/helpers'
import { SiteContext } from '@lib/site-context'
import { useContext, useEffect, useRef } from 'react'
import { isMarketingConsentGranted } from './consent'

/**
 * Triggers Klaviyo "Viewed Product" event.
 */
export const triggerKlaviyoViewProductEvent = (
  locale: Locale,
  settings: SanityGeneralSettings | null,
  product?: SanityProductFragment,
  activeVariant?: SanityProductVariantFragment,
) => {
  if (
    !product ||
    !activeVariant ||
    !settings?.klaviyoOnsiteTracking ||
    !window.klaviyo ||
    !isMarketingConsentGranted()
  ) {
    return
  }

  const url = getAbsoluteProductUrl(locale, settings, product, activeVariant)
  const productImageUrl = getProductImageUrl(product, activeVariant)

  const klaviyoProductItem: KlaviyoProductItem = {
    ProductName: getFormattedProductTitle(locale, product.title),
    ProductID: `${product.productID}`,
    VariantID: `${activeVariant.variantID}`,
    SKU: activeVariant.sku,
    URL: url,
    Price: getPriceNumber(activeVariant.price),
  }

  // Product image
  if (productImageUrl) {
    klaviyoProductItem.ImageURL = productImageUrl
  }

  // Product compare price
  if (activeVariant.comparePrice) {
    klaviyoProductItem.CompareAtPrice = getPriceNumber(
      activeVariant.comparePrice,
    )
  }

  window.klaviyo.track('Viewed Product', klaviyoProductItem)
  window.klaviyo.trackViewedItem(klaviyoProductItem)
}

/**
 * Klaviyo identity setting hook.
 */
export const useKlaviyoIdentity = () => {
  const { settings } = useContext(SiteContext)

  const lastIdentifiedEmailRef = useRef<string | null>(null)

  const { user } = useUser()

  useEffect(() => {
    if (
      !isMarketingConsentGranted() ||
      !user?.isLoggedIn ||
      !user?.email ||
      !settings?.klaviyoOnsiteTracking ||
      !window.klaviyo ||
      lastIdentifiedEmailRef.current === user.email
    ) {
      return
    }

    window.klaviyo.identify({
      email: user.email,
    })
    lastIdentifiedEmailRef.current = user.email
  }, [settings, user])
}
